import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../common/spinner';
import { listAllTenants, saveTenant } from '../modules/tenantdata';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import TenantCompany from '../domain/tenantcompany';
import { toast } from 'react-toastify';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

const _ = require('lodash');

const styles = {
    textField: { mx: 1, width: '95%' },
    formControl: { mt: 2, mb: 1, mx: 1, width: '95%' },
};

// Only a couple of markets are supported today (see doc/sweden-expansion-currency-plan.md) -- extend this
// list once more tenant currencies are needed instead of switching back to free text.
const CURRENCIES = ['EUR', 'SEK'];

export default function TenantCompanyEditView(props) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const tenantId =
        props.match.params.tenantCompanyId === 'new'
            ? props.match.params.tenantCompanyId
            : parseInt(props.match.params.tenantCompanyId, 10);

    const allTenants = useSelector((state) => state.tenantdata.allTenants);
    const isFetchingAllTenants = useSelector((state) => state.tenantdata.isFetchingAllTenants);
    const countryCodes = useSelector((state) => state.codedata.countryCodes) || [];

    const [saving, setSaving] = useState(false);
    const [createOnProgress, setCreateOnProgress] = useState(false);
    const [tenantCompany, setTenantCompany] = useState(null);
    const [formOk, setFormOk] = useState(false);

    useEffect(() => {
        if (tenantId === 'new' && !tenantCompany) {
            setCreateOnProgress(true);
            TenantCompany.NewObject().then(function (tc) {
                setTenantCompany(tc);
                setCreateOnProgress(false);
            });
        }
    }, [tenantId, tenantCompany]);

    useEffect(() => {
        if (!isFetchingAllTenants && (allTenants == null || allTenants.length === 0)) {
            dispatch(listAllTenants());
        } else if (tenantId !== 'new' && !isFetchingAllTenants) {
            let tenantToEdit = _.find(allTenants, ['id', tenantId]);
            setTenantCompany(tenantToEdit);
            setFormOk(true);
        }
    }, [allTenants, dispatch, isFetchingAllTenants, tenantId]);

    const checkIfFormOk = (newTenantCompany) => {
        let ok = true;
        if (newTenantCompany.name == null || newTenantCompany.name.length === 0) ok = false;
        if (newTenantCompany.currency == null || newTenantCompany.currency.length === 0) ok = false;
        if (newTenantCompany.countryCode == null) ok = false;
        if (ok !== formOk) {
            setFormOk(ok);
        }
    };

    const handleChange = (event) => {
        let newTenantCompany = new TenantCompany(tenantCompany);
        const target = event.target;
        const value = target.value;
        const name = target.name;
        switch (name) {
            case 'tenantName':
                newTenantCompany.name = value;
                break;
            case 'tenantCountryCode':
                newTenantCompany.countryCode = value === '' ? null : parseInt(value, 10);
                break;
            case 'tenantCurrency':
                newTenantCompany.currency = value;
                break;
            case 'tenantVatRate':
                newTenantCompany.vatRate = value === '' ? 0 : parseFloat(value);
                break;
            case 'tenantOrgNumber':
                newTenantCompany.orgNumber = value;
                break;
            case 'tenantVatId':
                newTenantCompany.vatId = value;
                break;
            case 'tenantAccountingIntegration':
                newTenantCompany.accountingIntegration = value;
                break;
            case 'tenantOrderNumberPrefix':
                newTenantCompany.orderNumberPrefix = value === '' ? null : value.toUpperCase();
                break;
            default:
                break;
        }

        setTenantCompany(newTenantCompany);
        checkIfFormOk(newTenantCompany);
    };

    const onCancel = () => {
        props.history.goBack();
    };

    const onSubmit = () => {
        let toBeSaved = new TenantCompany(tenantCompany);
        toast.info(t('toast.saving'), { autoClose: 1000, hideProgressBar: true });
        setSaving(true);
        dispatch(saveTenant(toBeSaved))
            .then(() => {
                if (tenantId === 'new') {
                    props.history.replace('/admin/tenantcompany/edit/' + toBeSaved.id);
                }
                toast.info(t('admin.tenantCompanySaved'), { autoClose: 1500, hideProgressBar: true });
                setSaving(false);
            })
            .catch(() => {
                toast.error(t('admin.tenantCompanySaveFailed'), { autoClose: 5000, hideProgressBar: false });
                setSaving(false);
            });
    };

    const getHeader = () => {
        if (tenantCompany == null) return null;
        return (
            <h2>
                <Button onClick={onCancel}>
                    <i className="fas fa-chevron-left fa-2x"></i>&nbsp;&nbsp;
                </Button>
                {tenantCompany.name}
            </h2>
        );
    };

    const renderView = () => {
        if (createOnProgress) {
            return <Spinner padding="20px" title={t('general.retrievingInformation')} />;
        }
        if (isFetchingAllTenants) {
            return <Spinner padding="20px" title={t('general.retrievingInformation')} />;
        } else if (!tenantCompany) {
            return (
                <div style={{ padding: '20px' }}>
                    <Alert severity="error">{t('admin.tenantCompanyLoadFailed')}</Alert>
                </div>
            );
        }

        return (
            <fieldset disabled={saving}>
                <Grid container>
                    <Grid item xs={12}>
                        {getHeader()}
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            required
                            error={tenantCompany.name == null || tenantCompany.name.length === 0}
                            id="tenantName"
                            name="tenantName"
                            label={t('general.name')}
                            value={tenantCompany.name}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl sx={styles.formControl} required error={tenantCompany.countryCode == null}>
                            <InputLabel id="tenantCountryCode-label">{t('admin.countryCode')}</InputLabel>
                            <Select
                                labelId="tenantCountryCode-label"
                                id="tenantCountryCode"
                                name="tenantCountryCode"
                                value={tenantCompany.countryCode == null ? '' : tenantCompany.countryCode}
                                onChange={handleChange}>
                                {countryCodes.map((code) => (
                                    <MenuItem key={code.countryId} value={code.countryId}>
                                        {code.texts[0].text} ({code.countryShortStr2})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl
                            sx={styles.formControl}
                            required
                            error={tenantCompany.currency == null || tenantCompany.currency.length === 0}>
                            <InputLabel id="tenantCurrency-label">{t('admin.currency')}</InputLabel>
                            <Select
                                labelId="tenantCurrency-label"
                                id="tenantCurrency"
                                name="tenantCurrency"
                                value={tenantCompany.currency || ''}
                                onChange={handleChange}>
                                {CURRENCIES.map((currency) => (
                                    <MenuItem key={currency} value={currency}>
                                        {currency}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            id="tenantVatRate"
                            name="tenantVatRate"
                            type="number"
                            label={t('admin.vatRate')}
                            value={tenantCompany.vatRate}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            id="tenantOrgNumber"
                            name="tenantOrgNumber"
                            label={t('admin.orgNumber')}
                            value={tenantCompany.orgNumber || ''}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            id="tenantVatId"
                            name="tenantVatId"
                            label={t('admin.vatId')}
                            value={tenantCompany.vatId || ''}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            id="tenantAccountingIntegration"
                            name="tenantAccountingIntegration"
                            label={t('admin.accountingIntegration')}
                            value={tenantCompany.accountingIntegration || ''}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            id="tenantOrderNumberPrefix"
                            name="tenantOrderNumberPrefix"
                            label={t('admin.orderNumberPrefix')}
                            helperText={t('admin.orderNumberPrefixHelperText')}
                            value={tenantCompany.orderNumberPrefix || ''}
                            inputProps={{ maxLength: 10 }}
                            sx={styles.textField}
                            margin="normal"
                            onChange={handleChange}
                        />
                    </Grid>

                    <Footer>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                name="save-tenantcompany"
                                onClick={onSubmit}
                                disabled={saving || !formOk}>
                                {saving && <i className="fas fa-spinner fa-spin"></i>}
                                {!saving && <i className="fas fa-save"></i>}
                                &nbsp;{t('buttons.save')}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="primary" name="close" onClick={onCancel} disabled={saving}>
                                <i className="far fa-window-close"></i>&nbsp;{t('buttons.close')}
                            </Button>
                        </Grid>
                    </Footer>
                    <FooterPadding />
                </Grid>
            </fieldset>
        );
    };

    return renderView();
}
